import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {v4 as getUUID} from 'uuid';
import {issueAttributesType} from '../../constants/proptype-validation';
import {canAddAttachment} from '../../utils/issues-helpers';
import i18n from '../../utils/i18n';
import Loader from '@adsk/bim360-matrix-react-loader';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Dropzone from '@adsk/bim360-matrix-react-dropzone';
import FilePicker from '@adsk/bim360-matrix-react-file-picker';
import {getAllowedFiles} from '../../utils/attachmentsHelper';
import {createGetFolderContentsMethod, createHasMoreFolderContents} from '../../utils/docsApiHelpers';
import IssueAttachmentsList from './IssueAttachmentsList';
import classNames from 'classnames';
import {viewTypes} from '../../constants/view-types';
import {attachmentOrigin} from '../../constants/attachments';
import {
  clearFailedUploadingAttachments,
  createAttachmentFromDocument,
  fetchAttachmentsAction,
  fetchFolderContents,
  fetchProjectFolders,
  uploadAttachmentFiles
} from '../../app/actions';
import noop from 'lodash/noop';
import {getCurrentViewType, getIsLoadingAttachments, getUploadingAndExistingAttachments} from '../../app/selectors';
import {connect} from '../../app/connect';

const useCloseFilePickerDocumentSelectionModal = () => {
  const [filePickerKey, setFilePickerKey] = useState(0);
  const closeDocumentSelectionModal = useCallback(() => {
    setFilePickerKey(key => key + 1);
  }, []);
  return {
    filePickerKey,
    closeDocumentSelectionModal
  };
};

const IssuePreviewAttachmentsTab = ({issue, isLoading, attachments, container, viewType, dispatch}) => {
  const {filePickerKey, closeDocumentSelectionModal} = useCloseFilePickerDocumentSelectionModal();

  useEffect(() => {
    dispatch(fetchAttachmentsAction({issueId: issue.id, offset: 0}));
    return () => {
      dispatch(clearFailedUploadingAttachments());
    };
  }, [dispatch, issue.id]);

  const handleUploadFiles = useCallback(files => {
    dispatch(
      uploadAttachmentFiles(
        [...files].map(file => ({id: getUUID(), file})),
        issue.id,
        true,
        attachmentOrigin.ATTACHMENT_TAB
      )
    );
    closeDocumentSelectionModal();
  }, [closeDocumentSelectionModal, dispatch, issue.id]);

  const handleChoosingDocument = useCallback((documentUrn, document) => {
    dispatch(createAttachmentFromDocument(document, issue.id, attachmentOrigin.ATTACHMENT_TAB));
    closeDocumentSelectionModal();
  }, [closeDocumentSelectionModal, dispatch, issue.id]);

  const cachedMethodsRef = useRef({
    getFolderContents: noop,
    hasMoreFolderContents: noop
  });

  const getProjectFolders = useCallback(() => {
    const documentsCache = {};
    // This method is called every time we load the tree from scratch. That's why we need to clean the cache here - for it to be relevant for the current tree.
    cachedMethodsRef.current = {
      getFolderContents: createGetFolderContentsMethod(documentsCache, (urn, options, resolve, reject) => {
        dispatch(fetchFolderContents(urn, options, resolve, reject));
      }),
      hasMoreFolderContents: createHasMoreFolderContents(documentsCache)
    };

    return new Promise((resolve, reject) => {
      dispatch(fetchProjectFolders(resolve, reject));
    });
  }, [dispatch]);

  const getFolderContents = urn => cachedMethodsRef.current.getFolderContents(urn);

  const scrollableContainerRef = useRef();

  if (isLoading && attachments.length === 0) {
    return <Loader />;
  }

  const canAddAttachments = canAddAttachment(issue);

  const contentClasses = classNames('IssuePreviewAttachmentsTab__content', {
    'IssuePreviewAttachmentsTab__content--empty': attachments.length === 0,
    'IssuePreviewAttachmentsTab__content--narrow': viewType === viewTypes.DOCUMENT,
    'IssuePreviewAttachmentsTab__content--wide': viewType === viewTypes.PROJECT
  });

  return (
    <div className="IssuePreviewAttachmentsTab" data-testid="IssuePreviewAttachmentsTab">
      <Dropzone onDrop={handleUploadFiles} accept={getAllowedFiles()} disabled={!canAddAttachments}>
        <div className={contentClasses} ref={scrollableContainerRef}>
          {canAddAttachments ? (
            <FilePicker
              key={filePickerKey}
              container={container}
              allowUploadMultipleFiles // Allow upload multiple files.
              multi={false} // Don't allow multiple Docs documents.
              allowDocsDocuments
              acceptFileTypes={getAllowedFiles()}
              openDocsUrn={handleChoosingDocument}
              uploadFiles={handleUploadFiles}
              getRootFolders={getProjectFolders}
              getFolderContents={getFolderContents}
              hasMoreFolderContents={cachedMethodsRef.current.hasMoreFolderContents}
              styleType={attachments.length ? FilePicker.STYLE.BAR : FilePicker.STYLE.LARGE}
            />
          ) : null}
          {attachments.length > 0 ? (
            <IssueAttachmentsList container={scrollableContainerRef.current} issueId={issue.id} />
          ) : canAddAttachment(issue) ? null : (
            <EmptyState icon={EmptyState.Icons.EMPTY_TEMPLATE} text={i18n.t('attachments.noAttachments')} />
          )}
        </div>
      </Dropzone>
    </div>
  );
};

IssuePreviewAttachmentsTab.displayName = 'IssuePreviewAttachmentsTab';

IssuePreviewAttachmentsTab.propTypes = {
  issue: issueAttributesType,
  isLoading: PropTypes.bool.isRequired,
  attachments: PropTypes.array.isRequired,
  container: PropTypes.object.isRequired,
  viewType: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  isLoading: getIsLoadingAttachments(state),
  attachments: getUploadingAndExistingAttachments(state),
  viewType: getCurrentViewType(state)
});

export default connect(mapStateToProps)(IssuePreviewAttachmentsTab);
